import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { notaTone } from '../lib/app';
import { usePageMeta } from '../lib/ui';
import { IconAlert, IconMoon, IconSun, IconX } from './icons';

/** Marca em mono, como no site: "licita" + ".dev" na cor de acento. */
export function Wordmark({ size = 14, soIcone = false }: { size?: number; soIcone?: boolean }) {
  return (
    <span className="inline-flex items-center gap-2 font-mono font-semibold tracking-tight" style={{ fontSize: size }}>
      <svg width={size + 6} height={size + 6} viewBox="0 0 64 64" aria-hidden="true">
        <rect width="64" height="64" rx="14" fill="#0A0B10" />
        <path d="M20 14v30h20" fill="none" stroke="#ECEDF2" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M30 38l6 6 12-14" fill="none" stroke="#7B86FF" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span className={soIcone ? 'sr-only' : ''}>
        licita<span className="text-accent">.dev</span>
      </span>
    </span>
  );
}

type Theme = 'light' | 'dark';
const currentTheme = (): Theme => (document.documentElement.dataset.theme === 'light' ? 'light' : 'dark');

/** Tema atual (data-theme no <html>) e a troca, gravada em localStorage('theme'), lida por /theme.js. */
export function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>('dark');
  useEffect(() => {
    setTheme(currentTheme());
    const mo = new MutationObserver(() => setTheme(currentTheme()));
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => mo.disconnect();
  }, []);
  const toggle = () => {
    const t: Theme = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.dataset.theme = t;
    try {
      localStorage.setItem('theme', t);
    } catch {}
  };
  return [theme, toggle];
}

export function ThemeToggle({ className = 'app-btn app-btn-ghost size-9 shrink-0 px-0 text-fg-2' }: { className?: string }) {
  const [theme, toggle] = useTheme();
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label="Alternar tema claro/escuro"
      aria-pressed={theme === 'dark'}
      title="Alternar tema"
      className={className}
    >
      {theme === 'dark' ? <IconSun /> : <IconMoon />}
    </button>
  );
}

/**
 * Cabeçalho da página: em telas largas o título e o subtítulo vão para o cabeçalho da moldura (via `usePageMeta`);
 * aqui ficam as ações e, no celular, o título.
 */
export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  usePageMeta(title, subtitle);
  return (
    <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0 lg:hidden">
        <h1 className="text-[20px] font-semibold tracking-[-0.2px]">{title}</h1>
        {subtitle && <p className="mt-0.5 text-[12px] text-mute">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2 lg:ml-auto">{actions}</div>}
    </header>
  );
}

export function ErrorBox({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="flex items-start gap-2 rounded-lg px-3 py-2 text-[13px] text-bad" style={{ background: 'var(--bad-soft)' }}>
      <span className="mt-px shrink-0">
        <IconAlert size={15} />
      </span>
      <span>{children}</span>
    </p>
  );
}

export function Notice({ children }: { children: ReactNode }) {
  return (
    <p role="status" className="rounded-lg px-3 py-2 text-[13px] text-accent" style={{ background: 'var(--accent-soft)' }}>
      {children}
    </p>
  );
}

const TONES = { ok: 'app-chip-ok', warn: 'app-chip-warn', bad: 'app-chip-bad', info: 'app-chip-accent', muted: '' } as const;
export function Chip({ tone = 'muted', children, mono }: { tone?: keyof typeof TONES; children: ReactNode; mono?: boolean }) {
  return <span className={`app-chip ${TONES[tone]} ${mono ? 'font-mono tabular-nums' : ''}`}>{children}</span>;
}

export function Skeleton({ h = 120, className = '' }: { h?: number; className?: string }) {
  return (
    <div className={`animate-pulse rounded-xl bg-bg-3 motion-reduce:animate-none ${className}`} style={{ height: h }} aria-busy="true" />
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <div className="app-card border-dashed py-8 text-center leading-relaxed text-fg-2">{children}</div>;
}

const RING = { ok: 'var(--ok)', warn: 'var(--warn)', bad: 'var(--bad)', muted: 'var(--mute)', info: 'var(--accent)' } as const;

/**
 * Nota 0–100 como anel (conic-gradient) com o número no meio. `fundo` é a cor do miolo (a do cartão onde o anel está).
 * Sem nota: anel vazio com "—".
 */
export function NotaRing({ nota, size = 30, fundo = 'var(--bg2)' }: { nota: number | null; size?: number; fundo?: string }) {
  const tone = notaTone(nota);
  const pct = nota == null ? 0 : Math.max(0, Math.min(100, nota));
  const miolo = size - 8;
  return (
    <span
      role="img"
      aria-label={nota == null ? 'Sem nota' : `Nota ${nota} de 100`}
      title="Chance estimada de participar e ser competitiva, de 0 a 100"
      className="flex shrink-0 items-center justify-center rounded-full"
      style={{ width: size, height: size, background: `conic-gradient(${RING[tone]} 0 ${pct}%, var(--line) ${pct}% 100%)` }}
    >
      <span
        className="flex items-center justify-center rounded-full font-mono font-semibold tabular-nums"
        style={{ width: miolo, height: miolo, background: fundo, fontSize: Math.max(10, Math.round(size / 3)) }}
      >
        {nota ?? '—'}
      </span>
    </span>
  );
}

/**
 * Botão que abre um painel flutuante logo abaixo (menus e filtros). Esc e clique fora fecham e o foco volta ao botão.
 * `children` recebe `fechar` para fechar depois de uma escolha.
 */
export function Popover({
  rotulo,
  botao,
  children,
  alinhar = 'esquerda',
  largura = 260,
  className = '',
}: {
  /** Nome acessível do painel (aria-label). */
  rotulo: string;
  /** Renderiza o botão; recebe o estado e os atributos aria. */
  botao: (p: { aberto: boolean; abrir: () => void; props: Record<string, unknown> }) => ReactNode;
  children: (fechar: () => void) => ReactNode;
  alinhar?: 'esquerda' | 'direita';
  largura?: number;
  className?: string;
}) {
  const [aberto, setAberto] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const painel = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (!aberto) return;
    // Foco no primeiro controle do painel (ou no próprio painel).
    const alvo = painel.current?.querySelector<HTMLElement>('input, select, button, [tabindex="0"]');
    (alvo ?? painel.current)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        fechar();
      }
    };
    const onDown = (e: MouseEvent) => {
      if (!raiz.current?.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [aberto]);

  const fechar = () => {
    setAberto(false);
    raiz.current?.querySelector<HTMLElement>('[aria-controls]')?.focus();
  };

  return (
    <div ref={raiz} className={`relative ${className}`}>
      {botao({
        aberto,
        abrir: () => setAberto((v) => !v),
        props: { 'aria-haspopup': 'dialog', 'aria-expanded': aberto, 'aria-controls': id },
      })}
      {aberto && (
        <div
          id={id}
          ref={painel}
          role="dialog"
          aria-label={rotulo}
          tabIndex={-1}
          className={`app-pop absolute top-[calc(100%+6px)] z-30 max-w-[calc(100vw-32px)] p-3 outline-none ${alinhar === 'direita' ? 'right-0' : 'left-0'}`}
          style={{ width: largura }}
        >
          {children(fechar)}
        </div>
      )}
    </div>
  );
}

/** Botão pequeno de fechar (ícone ×), para gavetas e painéis. */
export function BotaoFechar({ onClick, rotulo = 'Fechar' }: { onClick: () => void; rotulo?: string }) {
  return (
    <button type="button" className="app-btn app-btn-ghost size-8 min-h-0 shrink-0 px-0 text-mute" aria-label={rotulo} onClick={onClick}>
      <IconX size={14} />
    </button>
  );
}
